// customerCacheService.js
import localforage from 'localforage';
import axios from 'axios';

const customerStore = localforage.createInstance({
  name: 'dukakit_pos',
  storeName: 'customers'
});

const CACHE_KEY = 'customers';
const TIMESTAMP_KEY = 'customers_last_updated';
const IDENTITY_KEY = 'customers_identity';
const ONE_HOUR = 60 * 60 * 1000;

// Identity the cache was populated for (company/branch). Guards against
// serving another tenant's cached customer list if a branch switch or
// logout ever happens without going through clearCache() first.
function currentIdentity() {
  try {
    const userDetails = JSON.parse(window.localStorage.getItem('userDetails'));
    return (userDetails && userDetails.company && userDetails.company.id) || null;
  } catch (e) {
    return null;
  }
}

export default {
  async getCustomers(searchTerm = '', force = false, silent = false) {
    const isSearching = String(searchTerm).trim().length > 0;


    if (isSearching) {

      const response = await axios.get(`/api/allcustomers?perPage=10&search=${searchTerm}`);
      return response.data.data;
    }

    const now = Date.now();
    const lastUpdated = await customerStore.getItem(TIMESTAMP_KEY);
    const cachedForId = await customerStore.getItem(IDENTITY_KEY);
    const identityMatches = cachedForId && cachedForId === currentIdentity();
    const isCacheFresh = lastUpdated && identityMatches && (now - lastUpdated < ONE_HOUR);

    if (!force && isCacheFresh) {

      const cached = await customerStore.getItem(CACHE_KEY);
      if (cached) return cached;
    }


    try {
      // _silent: suppresses the app-wide loading indicator (see app.js's
      // axios interceptor) for background refreshes - see the matching note
      // in productCacheService.js's getProducts().
      const response = await axios.get('/api/allcustomers?perPage=1000', { _silent: silent }); // large prefetch
      const customers = response.data.data;

      await customerStore.setItem(CACHE_KEY, customers);
      await customerStore.setItem(TIMESTAMP_KEY, now);
      await customerStore.setItem(IDENTITY_KEY, currentIdentity());

      return customers;
    } catch (e) {
      console.warn('⚠️ Backend fetch failed, falling back to cached customers');
      // Only fall back to the cache if it belongs to the currently logged-in
      // identity — never serve another company's cached customers.
      if (identityMatches) {
        const fallback = await customerStore.getItem(CACHE_KEY);
        if (fallback) return fallback;
      }
      throw new Error('❌ Unable to fetch customers and no cached data available.');
    }
  },

  async clearCache() {
    await customerStore.removeItem(CACHE_KEY);
    await customerStore.removeItem(TIMESTAMP_KEY);
    await customerStore.removeItem(IDENTITY_KEY);
  }
};
